import { useEffect, useState } from "react";

import { App, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  EmailTypeCategories,
  EmailTypes,
  Frequency,
} from "@/shared/utils/constants";
import {
  useGetEmailSettingsForUserQuery,
  usePatchEmailSettingsMutation,
} from "@/redux/service";
import BaseButton from "@/components_v2/Button/BaseButton";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import BaseSwitch from "@/components_v2/FormFields/Switch/BaseSwitch";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  EmailSettingsMap,
  EmailSettingState,
  SettingCategory,
  SettingTypeEntry,
} from "@/features/users/settings/types";

import "../styles.css";

const EmailSettings = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const { data: emailSettingsData, isSuccess: isEmailSettingsSuccess } =
    useGetEmailSettingsForUserQuery();
  const [patchEmailSettings] = usePatchEmailSettingsMutation();
  const currentUser = useSelector(selectCurrentUser);

  const [emailSettings, setEmailSettings] = useState<EmailSettingsMap | null>(
    null,
  );
  const [frequency, setFrequency] = useState<string | undefined>(undefined);

  const emailTypeEntries = Object.values(EmailTypes).filter(
    (entry): entry is SettingTypeEntry => typeof entry !== "function",
  );

  useEffect(() => {
    if (isEmailSettingsSuccess) {
      const digestFrequency = emailSettingsData.find(
        (i) => i.type === EmailTypes.DIGEST.value,
      )?.frequency;
      setFrequency(digestFrequency);
      const settings = emailSettingsData.reduce<EmailSettingsMap>((acc, i) => {
        acc[i.type] = {
          id: i.id,
          enabled: i.enabled,
        };
        return acc;
      }, {});
      setEmailSettings(settings);
    }
  }, [emailSettingsData, isEmailSettingsSuccess]);

  useEffect(() => {
    if (emailSettings) {
      const digestFrequency = emailSettingsData?.find(
        (i) => i.type === EmailTypes.DIGEST.value,
      )?.frequency;
      if (digestFrequency !== frequency) {
        onChangeEmailDigestSetting(
          EmailTypes.DIGEST.value,
          emailSettings[EmailTypes.DIGEST.value]?.enabled,
        );
      }
    }
  }, [frequency]);

  const handleChangeForEmailType = (emailType: string, value: boolean) => {
    const setting = emailSettings?.[emailType];
    if (!setting) return;
    patchEmailSettings({ id: setting.id, data: { enabled: value } })
      .unwrap()
      .then(() => {
        notification.success({
          message: t("users.labels.emailSettingUpdated"),
        });
      });
  };

  const handleDisableAll = (category: SettingCategory) => {
    if (!emailSettings) return;
    const promises = emailTypeEntries
      .filter((type) => type.category === category.category)
      .map((type) => emailSettings[type.value])
      .filter((setting): setting is EmailSettingState => !!setting?.enabled)
      .map((setting) =>
        patchEmailSettings({
          id: setting.id,
          data: { enabled: false },
        }),
      );

    Promise.all(promises).then(() => {
      notification.success({ message: t("users.labels.emailSettingUpdated") });
    });
  };

  const onChangeEmailDigestSetting = (
    notificationType: string,
    value?: boolean,
  ) => {
    const setting = emailSettings?.[notificationType];
    if (!setting) return;
    patchEmailSettings({ id: setting.id, data: { enabled: value, frequency } })
      .unwrap()
      .then(() => {
        notification.success({
          message: t("users.labels.emailSettingsUpdated"),
        });
      });
  };

  if (!currentUser || !emailSettings) return <BaseSpinner />;

  return (
    <div className="page-layout">
      {Object.values(EmailTypeCategories).map((category) => {
        const orderedEntriesForCategory = emailTypeEntries
          .filter((type) => type.category === category.category)
          .filter((type) => emailSettings[type.value]);

        const areAllDisabled = orderedEntriesForCategory.every(
          (entry) => !emailSettings[entry.value]?.enabled,
        );

        return (
          <div
            className="user-settings-card flex flex-col gap-300"
            key={category.category}
          >
            <div className="flex items-center justify-between gap-200">
              <h3 className="header-m-medium">{t(category.labelKey)}</h3>
              <BaseButton
                variant={BUTTON_VARIANTS.LINK}
                size={BUTTON_SIZES.SMALL}
                disabled={areAllDisabled}
                onClick={() => handleDisableAll(category)}
              >
                {t("users.labels.disableAll")}
              </BaseButton>
            </div>

            <div>
              {orderedEntriesForCategory.map((entry) => (
                <div className="user-settings-toggle-row" key={entry.value}>
                  <span className="body-m-regular">{t(entry.labelKey)}</span>
                  <div className="flex items-center gap-200">
                    {category.category ===
                      EmailTypeCategories.COMMUNITY.category &&
                      entry.value === EmailTypes.DIGEST.value && (
                        <Select
                          className="user-settings-frequency-select"
                          defaultValue={Frequency.DAILY.value}
                          value={frequency}
                          onChange={(value) => setFrequency(value)}
                          disabled={
                            !emailSettings[EmailTypes.DIGEST.value]?.enabled
                          }
                          options={Frequency.asList()}
                        />
                      )}
                    <BaseSwitch
                      checked={emailSettings[entry.value]?.enabled}
                      onChange={(e) => handleChangeForEmailType(entry.value, e)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EmailSettings;
